import { formatAddressLabel } from 'common/helpers/address.helper';
import { IGuestRead } from 'common/services/residents-service/residents.service.types';
import { IAddress } from 'common/services/street-service/street.service.types';
import React, { forwardRef } from 'react';
import dayjs from 'dayjs';
import { GetDirectionIcon } from 'common/helpers/direction.helper';
import EIQLogoIcon from 'assets/icons/entranceiq-logo-black.png';
import { ServerGuestValidity } from 'common/enums/guest-validity.enum';
import { QRCode } from 'antd';
import { IPrintRoute } from 'features/eiq-secure/services/route-for-print';

import './eiq-secure-gust-license.scss';

const startEndChar = '|';

export const formatEiqPassportCode = (code: string) => {
  return `${startEndChar}${code}${startEndChar}`;
};

interface IEiqSecureGuestPrintInstructionProps {
  id?: string;
  address?: IAddress;
  route?: IPrintRoute | null;
  routeOriginAddressString?: string | null;
  guestName?: string | null;
  guestLicense?: string | null;
  gate?: string | null;
  rules?: string | null;
  communityName: string;
  isMinimize?: boolean | null;
  guest?: IGuestRead | null;
  fastAccessPassId?: string | null;
  speedLimit?: number | null;
  speedLimitImageUrl?: string | null;
  communityLogoUrl?: string | null;
}

const EiqSecureGuestPrintInstruction = forwardRef<HTMLDivElement, IEiqSecureGuestPrintInstructionProps>(
  (
    {
      id,
      address,
      route,
      communityName,
      gate,
      guestLicense,
      guestName,
      rules,
      isMinimize,
      guest,
      fastAccessPassId,
      speedLimit,
      speedLimitImageUrl,
      communityLogoUrl,
      routeOriginAddressString,
    },
    ref,
  ) => {
    const splitAddress = (value?: string | null) => {
      if (!value) {
        return {
          first: '',
          second: '',
        };
      }

      const parts = value
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);

      if (parts.length <= 3) {
        return {
          first: parts.join(', '),
          second: '',
        };
      }

      return {
        first: parts.slice(0, -3).join(', '),
        second: parts.slice(-3).join(', '),
      };
    };

    const originAddress = splitAddress(routeOriginAddressString);
    const destinationAddress = splitAddress(formatAddressLabel(address));

    const getPassValidUntilSection = () => {
      if (guest?.validity === ServerGuestValidity.Permanent) {
        return (
          <>
            <span>Permanent Guest</span>
            <span className="days-of-week">{guest.daysOfWeek.map((i) => i.slice(0, 3)).join('-')}</span>
          </>
        );
      }
      if (guest?.validity === ServerGuestValidity.DateRange) {
        return (
          <>
            <span>{dayjs(guest.endDate).set('hour', 23).set('minute', 59).format('MM/DD/YYYY, hh:mm A')}</span>
          </>
        );
      }
      return (
        <>
          <span>{dayjs().format('MM/DD/YYYY,')} 11:59PM</span>
        </>
      );
    };

    return (
      <div className="license-print-instruction">
        <div id={id} className="license-print-instruction-content" ref={ref}>
          {!speedLimitImageUrl && speedLimit && (
            <div className="speed-limit-wrapper">
              <div className="speed">Speed</div>
              <div className="limit">Limit</div>
              <div className="speed-limit">{speedLimit}</div>
            </div>
          )}
          {speedLimitImageUrl && <img className="speed-limit-image" src={speedLimitImageUrl} alt="speed limit" />}
          <div className="license-print-instruction-header">
            <div>
              <span>{communityName}</span>
              <span>Community</span>
            </div>
            <div>
              <span>Guest license plate#:</span>
              <span>{guestLicense}</span>
            </div>
          </div>
          <div className="license-print-instruction-body">
            <div>
              <div className="pass-valid-period">
                <span>Pass Valid Until:</span>
                {getPassValidUntilSection()}
              </div>
              <div className="qr-code-field">
                {fastAccessPassId && <QRCode value={formatEiqPassportCode(fastAccessPassId)} size={240} bordered={false} errorLevel="H" />}
              </div>
              <div className="field">
                <span className="name">Guest Name:</span>
                <span>{guestName}</span>
              </div>

              <div className="field">
                <span className="name">Visiting:</span>
                <span>{formatAddressLabel(address)}</span>
              </div>

              <div className="field">
                <span className="name">Pass Issued:</span>
                <span>{dayjs().format('MM/DD/YYYY, hh:mm A')}</span>
              </div>
              <div className="field">
                <span className="name">Issued at:</span>
                <span>{gate}</span>
              </div>
              <div className="rules">Rules</div>
              {speedLimit && <div className="speed-limit">The speed limit is {speedLimit}mph</div>}
              <div>{rules}</div>
            </div>
            <div>
              <div className="directions-title">Directions</div>

              <div className="directions-details">
                {route && (
                  <>
                    Drive {route.distanceText ?? ''}
                    {route.durationText ? ` ${route.durationText}` : ''}
                  </>
                )}
              </div>

              {route?.warnings.map((warning, index) => (
                <div key={`route-warning-${index}`} className="restrictions">
                  {warning}
                </div>
              ))}

              <div className="address1">{originAddress.first}</div>

              {originAddress.second && <div className="address2">{originAddress.second}</div>}

              {route?.steps.map((step, index) => {
                return (
                  <div key={`step-${index}`}>
                    {!isMinimize && step.maneuver && <div>{GetDirectionIcon(step.maneuver)}</div>}
                    <div className={`instruction-step ${isMinimize ? 'minimize' : ''}`}>{step.instruction}</div>
                    <div className={`step-distance ${isMinimize ? 'minimize' : ''}`}>{step.distanceText}</div>
                  </div>
                );
              })}

              <div className="address1">{destinationAddress.first}</div>

              {destinationAddress.second && <div className="address2">{destinationAddress.second}</div>}
            </div>
          </div>
          <div className="license-print-instruction-footer">
            <div>
              <img className="logo" src={communityLogoUrl ? communityLogoUrl : EIQLogoIcon} alt="logo" />
            </div>
          </div>
        </div>
      </div>
    );
  },
);

export default EiqSecureGuestPrintInstruction;
